import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PageWrapper from "../../components/PageWrapper";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import useLanguage from "../../utils/useLanguage";
import BackToDashboard from "../../components/BackToDashboard";

// assets
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";

import axios from "axios";
import { UserAuth } from "../../utils/AuthContext";
import Loader from "../../components/Loader";
import PeriodSearchFilter from "../../components/PeriodSearchFilter";
import InformationBox from "../../components/InformationBox";
import ErrorBox from "../../components/ErrorBox";

function formatHours(hours) {
  console.log(hours);
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  return `${wholeHours}:${minutes}`;
}

const EmployeesAttendance = () => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);

  // STATES
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState({
    title: t("employeeName"),
    value: "employeeName",
    type: "text",
  });
  const [error, setError] = useState("");

  const [attendanceData, setAttendanceData] = useState([]);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [selectedMonth, setSelectedMonth] = useState({
    value: new Date().getMonth(),
    name: "",
  });
  const [selectedYear, setSelectedYear] = useState({
    value: new Date().getFullYear(),
    name: new Date().getFullYear().toString(),
  });

  // filter options
  const searchTitles = useMemo(
    () => [
      { title: t("employeeName"), value: "employeeName", type: "text" },
      { title: t("salary"), value: "salary", type: "money" },
      { title: t("workRate"), value: "workRate", type: "text" },
    ],
    [t]
  );

  // table titles
  const titles = [
    { title: "name", value: "name", keepOnMobile: true },
    { title: "workRate", value: "workRate", keepOnMobile: true },
    { title: "salary", value: "salary", keepOnMobile: true },
  ];

  const attendanceTable = {
    titles,
    rows: attendanceData.map((item) => [
      { value: item.name, type: TABLE_CELL_TYPES.TEXT },
      { value: formatHours(item.totalHours), type: TABLE_CELL_TYPES.TEXT },
      { value: item.hourlyRate, type: TABLE_CELL_TYPES.TEXT },
    ]),
  };

  // ENDPOINTS
  const fetchAttendance = async () => {
    const token = localStorage.getItem("token");
    try {
      setIsLoading(true);

      // Prepare request parameters
      const params = {
        page: pagination.currentPage,
        limit: 10,
        organizationId: parseInt(userData.organizationId),
      };

      // Only add search params if there's a search query and selected filter
      if (searchQuery && selectedFilter?.value) {
        params.search = searchQuery;
        params.searchField = selectedFilter.value;
      }

      // Only add month/year if they're selected
      if (selectedMonth) {
        params.month = parseInt(selectedMonth.value);
      }

      if (selectedYear) {
        params.year = parseInt(selectedYear.value);
      }

      const res = await axios.get(
        `${process.env.REACT_APP_URL}/api/attendance/getEmployeesAttendancies`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data.attendancies;

      const formattedRows = data.map((item) => [
        {
          value: item.name,
          type: TABLE_CELL_TYPES.LINK,
          to: `/employee-attendance/${item.userId}`,
          keepOnMobile: true,
        },
        {
          value: formatHours(item.workingHours),
          type: TABLE_CELL_TYPES.TEXT,
          keepOnMobile: true,
        },
        {
          value: item?.salary?.toFixed(2) + ` ${t("IQD")} ` || 0,
          type: TABLE_CELL_TYPES.TEXT,
          keepOnMobile: true,
        },
      ]);

      if (res.status === 200) {
        setAttendanceData(data);
        setRows(formattedRows);
        setPagination((prev) => ({
          ...prev,
          totalPages: res.data.totalPages,
          totalRecords: res.data.totalCount,
        }));
      }
    } catch (error) {
      setError(error.response.data.message);

      console.error("Error fetching attendance", error);
    } finally {
      setIsLoading(false);
    }
  };

  // HANDLERS

  const handleSearch = () => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    fetchAttendance();
  };

  // EFFECTS

  useEffect(() => {
    fetchAttendance();
  }, [pagination.currentPage, searchQuery]);

  return (
    <PageWrapper>
      {/* Back button */}
      <div>
        <BackToDashboard
          title={t("back")}
          variant="blank"
          icon={currentDirection === "ltr" ? previousIcon : nextIcon}
          iconSize={14}
          to={
            userData?.userType === "user"
              ? "/invoiceManagement"
              : "/attendance-dashboard"
          }
        />
      </div>

      <div className="o-row o-row--gap-size_100 t-bold_bold u-margin--top_400">
        <span>
          {userData?.userType === "organization"
            ? `${t("organization")}:`
            : `${t("user")}:`}
        </span>
        <span>{`${localStorage.getItem("username")}`}</span>
      </div>

      <h1>{t("workRate")}</h1>

      <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end u-margin--bottom_400">
        <PeriodSearchFilter
          options={searchTitles}
          selected={selectedFilter}
          setSelected={setSelectedFilter}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          handleSearch={handleSearch}
        />
      </section>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <ErrorBox>{t(error)}</ErrorBox>
      ) : rows.length == 0 ? (
        <InformationBox>{t("noAttendance")}</InformationBox>
      ) : (
        <RoundedTable
          titles={titles}
          rows={rows}
          pagination={pagination}
          setPagination={setPagination}
          mobileEffect
        />
      )}
    </PageWrapper>
  );
};

export default EmployeesAttendance;
